import React from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { FormikProps } from "formik";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import Role from "../../../enums/Role";
import { useAssignableRoles } from "hooks/useAssignableRoles";
import { useCompanyAppFeatureKeys } from "hooks/useCompanyAppFeatureKeys";
import StatusType from "../../../enums/UserStatus";
import { FormikAddUser } from "hooks/useUserProfileData";
import { FormikCustomField } from "hooks/customFields.types";
import { useFeatureEntitlements } from "@frontegg/react";
import AccountSearchAutoComplete from "components/common/AccountSearchAutoComplete";
import FormikCustomFields from "components/common/FormikCustomFields";
import { Stack } from "components/layout/reusableLayouts";
import { Select, TextField } from "@outpave/ds";

interface UserInformationProps {
  formik: FormikProps<FormikAddUser>;
  isNew?: boolean;
  customFieldSettings?: FormikCustomField[];
  /** Forwarded to the account field so it stays in sync with the drawer. */
  loading?: boolean;
}

const PlatformUserInformation: React.FC<UserInformationProps> = (props) => {
  const { formik, isNew, customFieldSettings, loading = false } = props;
  const { t } = useTranslation();
  const formikHelpers = useFormikHelpers(formik);
  const { isEntitled: featureCustomFields } = useFeatureEntitlements(
    "feature_card_custom_fields",
  );

  // Platform app can grant company and platform roles, subject to the actor's rank.
  const platformRoles: Role[] = [
    Role.Cardholder,
    Role.Employee,
    Role.Contractor,
    Role.Manager,
    Role.Auditor,
    Role.Accountant,
    Role.Admin,
    Role.PlatformAdmin,
    Role.PlatformOwner,
  ];
  const targetAppFeatureKeys = useCompanyAppFeatureKeys(
    formik.values.companyId,
  );
  const { options: roleOptions, platformAppMissing } = useAssignableRoles(
    platformRoles,
    formik.values.role as Role,
    formik.values.companyId,
    targetAppFeatureKeys,
  );

  return (
    <Stack gap="var(--space-5)">
      <Typography variant="body1">
        {t("Fill out the basic profile for this user")}
      </Typography>

      <AccountSearchAutoComplete
        title={t("PlatformAccount")}
        handleSelectAccount={(account) => {
          formik.setFieldValue("companyId", account?.id);
          formik.setFieldValue("company", account);
        }}
        errorText={formikHelpers.handleFormikHelperText("companyId")}
        account={formik.values.company}
        required={true}
        loading={loading}
      />

      <TextField
        name="firstName"
        label={t("First Name")}
        required={true}
        size="md"
        value={formik.values.firstName}
        fullWidth
        onChange={formik.handleChange}
        error={formikHelpers.handleFormikError("firstName")}
        helperText={formikHelpers.handleFormikHelperText("firstName")}
      />

      <TextField
        name="lastName"
        label={t("Last Name")}
        required={true}
        size="md"
        value={formik.values.lastName}
        fullWidth
        onChange={formik.handleChange}
        error={formikHelpers.handleFormikError("lastName")}
        helperText={formikHelpers.handleFormikHelperText("lastName")}
      />

      <TextField
        name="email"
        label={t("Email Address")}
        required={true}
        size="md"
        disabled={
          !isNew &&
          [StatusType.ACTIVE, StatusType.PENDING].includes(
            formik.values.status as StatusType,
          )
        }
        value={formik.values.email}
        fullWidth
        onChange={formik.handleChange}
        error={formikHelpers.handleFormikError("email")}
        helperText={formikHelpers.handleFormikHelperText("email")}
      />

      <Select
        label={t("User role")}
        required={true}
        size="md"
        value={formik.values.role}
        fullWidth
        onChange={(id) => formik.setFieldValue("role", id)}
        // handleFormikError returns a boolean, which the field reads as its
        // error *text* and renders as nothing; the helper function is the copy.
        errorText={formikHelpers.handleFormikHelperText("role") || undefined}
        // The package supersedes helperText with errorText, so a validation
        // message still wins over this explanation.
        helperText={
          platformAppMissing ? t("PlatformRolesNeedPlatformApp") : undefined
        }
        options={roleOptions.map(({ role, disabled }) => ({
          id: role,
          label: t(role),
          disabled,
        }))}
      />

      <Stack gap="0">
        {featureCustomFields && (
          <FormikCustomFields settings={customFieldSettings} formik={formik} />
        )}
      </Stack>
    </Stack>
  );
};

export default PlatformUserInformation;
